import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { ArrowRight, Loader2, Undo } from "lucide-react";
import { useState } from "react";
import {
  type MonthlySpend,
  monthlySpendOptions,
  type OrganizationSize,
  organizationSizeOptions,
  signUpStepValidators,
} from "shared";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/auth-card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import { useAppForm } from "@/hooks/useCreateForm";
import { authClient } from "@/lib/authClient";
import { novemberOfferUtm, utmTitles } from "../_unauthed";

const validateSearch = type({
  email: "string?",
  first: "string?",
  last: "string?",
  referral: "string?",
  utm: "string?",
});

export const Route = createFileRoute("/_unauthed/sign-up")({
  component: RouteComponent,
  validateSearch,
});

const signUpSteps = ["account-info", "metrics"] as const;

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const { utm, referral, email, first, last } = Route.useSearch();
  const { mutate, isPending } = useMutation(
    trpc.auth.signUpWithOrganization.mutationOptions({
      onError: (error) => {
        toast.error("Couldn't sign up", { description: error.message });
      },
      onSuccess: async (_data, vars) => {
        toast.success("Organization Created");
        await authClient.signIn.email({
          email: vars.email,
          password: vars.password,
        });
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());
      },
    }),
  );
  const { data } = useSuspenseQuery(
    trpc.referral.getReferralUser.queryOptions({
      code: referral as string,
    }),
  );
  let referralMessage = "";
  if (referral && data?.name) {
    referralMessage = `${data.name} has invited you to join Givefront.`;
  }

  const [step, setStep] =
    useState<(typeof signUpSteps)[number]>("account-info");
  const stepIndex = signUpSteps.indexOf(step);

  const form = useAppForm({
    defaultValues: {
      confirm: "",
      dbaName: "" as string,
      ein: "",
      email: email ?? "",
      enrollEmail: true,
      first: first ?? "",
      last: last ?? "",
      monthlySpend: "1-2.5k" satisfies MonthlySpend,
      organizationName: "",
      organizationSize: "2-5" satisfies OrganizationSize,
      password: "",
      phone: "",
      utm: (utm ? utm : null) as string | null,
    },
    onSubmit: async ({ value }) => {
      if (stepIndex === signUpSteps.length - 1)
        mutate({
          ...value,
          referralCode: referral,
        });
      else setStep(signUpSteps[stepIndex + 1]);
    },
    validators: {
      // @ts-ignore TypeScript necessitates validators that cover all fields, which we don't always need
      onSubmit: signUpStepValidators[stepIndex] ?? signUpClientOrganization,
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <AuthCard
        description={
          <Text as="span" variant="muted">
            Already have an account?{" "}
            <Link to="/login" variant="default">
              Sign in
            </Link>
          </Text>
        }
        title={
          utmTitles[(utm as keyof typeof utmTitles) ?? "default"] ??
          utmTitles.default
        }
        utm={utm}
      >
        <div className="flex flex-col gap-4">
          {referralMessage && (
            <h1 className="font-medium italic">{referralMessage}</h1>
          )}

          {step === "account-info" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <form.AppField
                  children={(field) => {
                    return <field.TextField label="First" required={true} />;
                  }}
                  name="first"
                />
                <form.AppField
                  children={(field) => {
                    return <field.TextField label="Last" required={true} />;
                  }}
                  name="last"
                />
              </div>
              <form.AppField
                children={(field) => {
                  return <field.TextField label="Email" required={true} />;
                }}
                name="email"
              />
              <form.AppField
                children={(field) => {
                  return (
                    <field.PhoneField
                      label="Cell Phone Number"
                      required={true}
                    />
                  );
                }}
                name="phone"
              />
              <div className="grid grid-cols-2 gap-3 pb-2">
                <form.AppField
                  children={(field) => {
                    return (
                      <field.PasswordField label="Password" required={true} />
                    );
                  }}
                  name="password"
                />
                <form.AppField
                  children={(field) => {
                    return (
                      <field.PasswordField
                        label="Confirm Password"
                        required={true}
                      />
                    );
                  }}
                  name="confirm"
                />
              </div>
            </>
          )}

          {step === "metrics" && (
            <>
              <form.AppField
                children={(field) => {
                  return (
                    <field.TextField
                      description="This must exactly match the name listed on your IRS documents including capitalization and punctuation."
                      label="Nonprofit Organization Legal Name"
                      required={true}
                    />
                  );
                }}
                name="organizationName"
              />
              <form.AppField
                children={(field) => {
                  return <field.EinField />;
                }}
                name="ein"
              />

              <form.AppField
                children={(field) => {
                  return (
                    <field.SliderSelectField
                      label="How large is your nonprofit?"
                      required={true}
                      options={organizationSizeOptions}
                    />
                  );
                }}
                name="organizationSize"
              />

              <form.AppField
                children={(field) => {
                  return (
                    <field.SliderSelectField
                      label="What are your typical monthly card expenses?"
                      required={true}
                      options={monthlySpendOptions}
                    />
                  );
                }}
                name="monthlySpend"
              />
            </>
          )}

          {stepIndex === 0 && (
            <>
              <form.AppField
                name="enrollEmail"
                children={(field) => {
                  return (
                    <field.CheckboxField
                      label={
                        <span className="text-muted-foreground text-sm">
                          Send me updates via email. Unsubscribe any time.
                        </span>
                      }
                    />
                  );
                }}
              />

              <span className="text-muted-foreground text-xs">
                I agree to receive recurring automated text messages at the
                phone number provided. Msg & data rates may apply. Msg frequency
                varies. Reply HELP for help and STOP to cancel. View our{" "}
                <a
                  className="underline"
                  href="https://www.givefront.com/legal/terms"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  className="underline"
                  href="https://www.givefront.com/legal/privacy"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </span>
            </>
          )}

          <div className="mt-2 flex items-center gap-6">
            {stepIndex !== 0 && (
              <button
                className="flex items-center gap-2 font-semibold text-neutral-500 text-sm transition-colors hover:text-neutral-700"
                onClick={() => setStep(signUpSteps[stepIndex - 1])}
                type="button"
              >
                <Undo className="size-4" /> Back
              </button>
            )}

            <Button
              className="w-fit grow"
              disabled={isPending}
              type="submit"
              variant="auth-primary"
            >
              {isPending && <Loader2 className="mr-2 animate-spin" />}
              {stepIndex < signUpSteps.length - 1 ? (
                <>
                  Continue <ArrowRight className="size-4" />
                </>
              ) : (
                "Sign Up and Start Onboarding"
              )}
            </Button>
          </div>

          {utm === novemberOfferUtm && (
            <span className="text-[0.6rem] text-muted-foreground">
              ¹Organizations may be eligible to receive a $150 statement credit
              after making at least $1,000 in eligible net purchases within the
              first three (3) months from the date the account is opened.
              Eligible purchases do not include fees, balance transfers, cash
              advances, refunds, or returns. The $150 statement credit will be
              applied within 30 days after the qualifying spend requirement is
              met. The account must be open, active, and in good standing at the
              time the credit is applied. Offer subject to change or
              cancellation at any time.
            </span>
          )}
        </div>
      </AuthCard>
    </form>
  );
}
